import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';

import { CallCenterDatabaseTable } from '../database-table/call-center-database-table.component';
import { CallCenterDatabaseTableData } from '../../../interfaces/call-center-database.interface';
import { SharedFiltersComponent } from '../../../shared/components/shared-filters/shared-filters.component';
import { UploadService } from '../../../services/upload.service';
import { AuthService } from '../../../services/auth.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ContractFilter } from '../../../interfaces/contract-filter.interface';
import { DatabaseDataParams } from '../../../interfaces/upload.interface';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-call-center-wrapper',
  imports: [
    CallCenterDatabaseTable,
    SharedFiltersComponent,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule
],
  templateUrl: './call-center-wrapper.component.html',
  styleUrl: './call-center-wrapper.component.scss'
})
export class CallCenterWrapperComponent implements OnInit, OnDestroy {
  title = 'Adatbázisom';
  databaseData: CallCenterDatabaseTableData[] = [];
  totalRecords = 0;
  isLoading = true;
  isFirstLoad = true; // Első betöltésnél spinner, utána loading overlay
  error: string | null = null;

  // Szerver oldali lapozás állapota
  currentPage = 0;
  currentPageSize = 25;
  currentSortBy = 'last_call_date';
  currentSortDir: 'asc' | 'desc' = 'desc';

  // Paginator storage key — azonos a table komponenssel a szinkronizáció érdekében
  private readonly PAGINATOR_STORAGE_KEY = 'call-center-paginator-settings';

  // Debounce a szöveges kereséshez
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Debounce a Főtevékenység mező javaslataihoz
  private mainActivitySubject = new Subject<string>();
  private mainActivitySubscription?: Subscription;
  mainActivityOptions: string[] = [];

  // Shared Filters State
  currentFilter: ContractFilter = {
    searchTerm: '',
    statusFilter: [],
    biztositoFilter: [],
    assigneeFilter: ['all'],
    dateFromFilter: null,
    dateToFilter: null,
    productFilter: [],
    paymentFrequencyFilter: [],
    currencyFilter: [],
    policyNumberFilter: '',
    mainActivityFilter: []
  };

  // Call center staff for assignee filtering
  callCenterStaff: any[] = [];

  // Call Center specific status options
  callCenterStatusOptions = [
    { value: 'hivando', label: 'Hívandó' },
    { value: 'visszahivando', label: 'Visszahívandó' },
    { value: 'elutasitott', label: 'Elutasított' },
    { value: 'robinson_listas', label: 'Robinson listás' },
    { value: 'nem_vette_fel', label: 'Nem vette fel' },
    { value: 'egyeztetett-kum', label: 'Egyeztetett (KÜM)' },
    { value: 'egyeztetett-vagyon', label: 'Egyeztetett (vagyon)' },
    { value: 'egyeztetett-kv', label: 'Egyeztetett (K+v)' }
  ];

  constructor(
    private uploadService: UploadService,
    private authService: AuthService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Debounce beállítása a szöveges kereséshez (300ms várakozás gépelés után)
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(() => {
      this.currentPage = 0; // Keresés mindig az első oldalra visz
      this.loadDatabaseData();
    });

    // Főtevékenység javaslatok debounce-olt lekérdezése
    this.mainActivitySubscription = this.mainActivitySubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => this.uploadService.getMainActivities(term).pipe(
        catchError(() => of({ success: false, activities: [] }))
      ))
    ).subscribe(response => {
      this.mainActivityOptions = response.activities || [];
      this.cdr.detectChanges();
    });

    // Minden felhasználó számára betöltjük a call center munkatársakat, kivéve az alapszintű call center munkatársakat
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && this.canManageCallCenterStaff()) {
      this.loadCallCenterStaff();
    }
    
    // Initialize filters based on user role
    this.initializeFiltersBasedOnRole();

    // Paginator mentett page size betöltése az első API hívás előtt
    // Ez biztosítja, hogy a backend annyi rekordot adjon vissza, amennyit a paginator mutat
    this.loadSavedPageSize();
    
    // Első adatbetöltés
    this.loadDatabaseData();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.searchSubject.complete();
    this.mainActivitySubscription?.unsubscribe();
    this.mainActivitySubject.complete();
  }

  /**
   * Főtevékenység javaslatok kérése a szűrő komponens gépelés eseményére
   */
  onMainActivitySearch(term: string): void {
    this.mainActivitySubject.next(term);
  }

  private loadCallCenterStaff() {
    this.authService.getCallCenterUsers().subscribe({
      next: (users: any[]) => {
        // Aktív felhasználók + inaktív felhasználók akikhez vannak hozzárendelt rekordok (backend szűri)
        this.callCenterStaff = users;
        },
      error: (error: any) => {
        this.callCenterStaff = [];
      }
    });
  }

  /**
   * Szerver oldali adatlekérdezés az aktuális szűrőkkel és lapozási állapottal
   */
  loadDatabaseData(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
    
    const params = this.buildQueryParams();
    
    this.uploadService.getDatabaseData(params).subscribe({
      next: (response) => {
        this.databaseData = response.data.map(item => ({
          ...item,
          date: item.date ? new Date(item.date) : undefined
        }));
        this.totalRecords = response.total_records;
        this.isLoading = false;
        this.isFirstLoad = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isLoading = false;
        this.isFirstLoad = false;
        this.cdr.detectChanges();
        this.snackBar.open('Hiba történt az adatok betöltése során', 'Bezárás', {
          duration: 3000
        });
      }
    });
  }

  /**
   * Query paraméterek összeállítása az aktuális szűrők és lapozás alapján
   */
  private buildQueryParams(): DatabaseDataParams {
    const filter = this.currentFilter;
    
    // Státusz szűrő átalakítása
    const statusArr = Array.isArray(filter.statusFilter) 
      ? filter.statusFilter.filter(s => s && s !== 'all')
      : (filter.statusFilter ? [filter.statusFilter] : []);
    
    // Hozzárendelés szűrő átalakítása
    let assigneeArr: string[] = [];
    if (Array.isArray(filter.assigneeFilter)) {
      assigneeArr = filter.assigneeFilter.filter(a => a && a !== 'all');
    } else if (filter.assigneeFilter && filter.assigneeFilter !== 'all') {
      assigneeArr = [filter.assigneeFilter as string];
    }
    
    // Dátum formázás ISO string-re (helyi időzóna szerint, nem UTC!)
    // A toISOString() UTC-t ad vissza, ami UTC+2 zónában -1 napot eredményezne
    const formatLocalDate = (d: Date): string => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const dateFrom = filter.dateFromFilter 
      ? (filter.dateFromFilter instanceof Date ? formatLocalDate(filter.dateFromFilter) : filter.dateFromFilter as string)
      : undefined;
    const dateTo = filter.dateToFilter 
      ? (filter.dateToFilter instanceof Date ? formatLocalDate(filter.dateToFilter) : filter.dateToFilter as string)
      : undefined;

    // Főtevékenység szűrő — csak a legördülő listából kiválasztott értékek
    const mainActivityArr = Array.isArray(filter.mainActivityFilter)
      ? filter.mainActivityFilter.filter(a => !!a)
      : [];

    return {
      page: this.currentPage,
      pageSize: this.currentPageSize,
      search: filter.searchTerm || undefined,
      status: statusArr.length > 0 ? statusArr : undefined,
      assignee: assigneeArr.length > 0 ? assigneeArr : undefined,
      mainActivity: mainActivityArr.length > 0 ? mainActivityArr : undefined,
      dateFrom,
      dateTo,
      sortBy: this.currentSortBy,
      sortDir: this.currentSortDir
    };
  }

  retryLoad() {
    this.loadDatabaseData();
  }

  onDataChanged() {
    this.loadDatabaseData();
  }

  /**
   * SharedFiltersComponent szűrőváltozás kezelése
   * Szöveges keresésnél debounce-olt, egyéb szűrőknél azonnali API hívás
   */
  onSharedFilterChange(filter: ContractFilter) {
    const previousSearchTerm = this.currentFilter.searchTerm;
    this.currentFilter = filter;

    // Ha a keresőmező változott → debounce-olt hívás
    if (filter.searchTerm !== previousSearchTerm) {
      this.searchSubject.next(filter.searchTerm);
      return;
    }

    // Egyéb szűrők változásakor azonnal visszaállítjuk az első oldalra és lekérdezünk
    this.currentPage = 0;
    this.loadDatabaseData();
  }

  /**
   * Lapváltás kezelése a table komponenstől
   */
  onPageChange(event: { pageIndex: number; pageSize: number }) {
    this.currentPage = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.loadDatabaseData();
  }

  /**
   * Rendezésváltozás kezelése a table komponenstől
   */
  onSortChange(event: { active: string; direction: string }) {
    this.currentSortBy = event.active || 'last_call_date';
    this.currentSortDir = (event.direction as 'asc' | 'desc') || 'desc';
    this.currentPage = 0; // Rendezés mindig az első oldalra visz
    this.loadDatabaseData();
  }

  // Property to hold the current filter data for the table (megmarad kompatibilitás miatt)
  currentFilterData: any = null;

  /**
   * Initialize filters based on user role
   */
  private initializeFiltersBasedOnRole() {
    const currentUser = this.authService.getCurrentUser();
    
    if (currentUser && currentUser.jobs?.includes('call_center')) {
      this.currentFilter = {
        ...this.currentFilter,
        assigneeFilter: [currentUser.username]
      };
    } else {
      this.currentFilter = {
        ...this.currentFilter,
        assigneeFilter: ['all']
      };
    }
  }

  isCallCenterEmployee(): boolean {
    const currentUser = this.authService.getCurrentUser();
    return !!(currentUser && currentUser.jobs?.includes('call_center'));
  }

  canManageCallCenterStaff(): boolean {
    const currentUser = this.authService.getCurrentUser();
    return !!(currentUser && currentUser.jobs && currentUser.jobs.some(j => ['admin', 'sales_manager', 'deputy-CEO'].includes(j)));
  }

  /**
   * Mentett paginator page size betöltése localStorage-ból
   * Ez biztosítja, hogy az első API hívás a helyes page size-t használja
   */
  private loadSavedPageSize(): void {
    try {
      const savedSettings = localStorage.getItem(this.PAGINATOR_STORAGE_KEY);
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        if (settings.pageSize && [10, 25, 50, 100].includes(settings.pageSize)) {
          this.currentPageSize = settings.pageSize;
        }
        // pageIndex-et NEM töltjük be — mindig page 0-ról indul a modul megnyitásakor
        // Ez megakadályozza a felesleges dupla API hívásokat
      }
    } catch (error) {
      console.error('Paginator beállítás olvasási hiba:', error);
    }
  }
}
